import { useEffect } from "react";

/**
 * ADA rove focus
 * 
 * @functions
 * @category Hooks
 * @summary Used to add Specific keyboard navigation looping abilities.
 * @param {Node} wrapper - Wrapper of the Node that contains the roving keyboard navigation
 * @param {number} back - KeyCode of the back button
 * @param {number} fwd - KeyCode of the forward
 * @example 
 * //result: all children in .tablist--type can be navigated with right and left arrows
 * useRoveFocus(document.querySelectorAll(`.tablist--type`)[0], 37, 39);
 */
const useRoveFocus = (ref, back, fwd) => {
   /**
    * Sets focus boundaries and focusable content
    * @param {Node} ref - Node to keep focus within
    */
    let  focusableElements = 'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

    useEffect(() => {

      const setFocusBoundaries = (ref) =>{
        let children = ref.current.querySelectorAll(focusableElements);
        children = Array.from(children).filter(item => (item.offsetWidth > 0 || item.offsetHeight > 0));
        let boundaries = {
            firstFocusableElement : children[0],
            focusableContent : children,
            lastFocusableElement : children[children.length -1]
        };
        return boundaries;
      };

      const handleKeyDown = (e) => {
        if(!ref.current) {
          return;
        }else{
          let code = (e.keyCode ? e.keyCode : e.which);
          let isArrowPressed = (code === back || code === fwd);
          let currentElement = document.activeElement;
          let tabFocused = ref.current.contains(currentElement);
          if(!isArrowPressed || !tabFocused) {
              return;
          } else {
            let boundaries = setFocusBoundaries(ref);
            if ( (code !== 9 && code === back) || ( code === 9 && e.shiftKey ) ) {
                // back
                e.preventDefault();
                if (currentElement === boundaries.firstFocusableElement) {
                    boundaries.lastFocusableElement.focus(); // add focus for the last focusable element
                } else {
                    //move back to the prev focusable element
                    boundaries.focusableContent[boundaries.focusableContent.indexOf(currentElement)-1]?.focus();
                };
            } else if (code === fwd) {
                // fwd
                e.preventDefault();
                if (currentElement === boundaries.lastFocusableElement) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
                    boundaries.firstFocusableElement.focus(); // add focus for the first focusable element
                };
                //move on to the next focusable element
                boundaries.focusableContent[boundaries.focusableContent.indexOf(currentElement)+1]?.focus();
            };
          };
        }
      }

      document.addEventListener("keydown", handleKeyDown);
      return () => {
          document.removeEventListener("keydown", handleKeyDown);
      };
    },[back, ref, focusableElements, fwd]
  );
};

export default useRoveFocus;
